import { apiRequest } from "./functionsClient";
import type { SBotMembershipTargetType } from "@/models/user";
import type { LeagueOrganizationAdminValue } from "@/models/schema";
import { organizationAdminValueFirestoreData } from "@/models/schema";

// Port of Services/SBotInviteService.swift — all invite flows go through the
// Cloud Function endpoints (sbotInvites writes are rules-denied client-side).

export type SBotInviteRole =
  | "leagueAdmin"
  | "leagueManager"
  | "leagueStaff"
  | "organizationAdmin"
  | "organizationManager"
  | "organizationStaff"
  | "athlete";
export type SBotInviteStatus = "sent" | "accepted" | "revoked" | "expired" | "failed";

export const INVITE_ROLE_LABELS: Record<SBotInviteRole, string> = {
  leagueAdmin: "League Admin",
  leagueManager: "League Manager",
  leagueStaff: "League Staff",
  organizationAdmin: "Organization Admin",
  organizationManager: "Organization Manager",
  organizationStaff: "Organization Staff",
  athlete: "Athlete",
};

export interface SBotInviteSummary {
  id: string;
  code: string;
  inviteeEmail: string;
  role: SBotInviteRole;
  targetType: SBotMembershipTargetType;
  leagueID: string;
  leagueName: string;
  sportID?: string;
  organizationID?: string;
  organizationName?: string;
  inviterUserID: string;
  inviterEmail: string;
  inviterName: string;
  message?: string;
  status: SBotInviteStatus;
  expiresAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SBotInviteDraft {
  inviteeEmail: string;
  role: SBotInviteRole;
  leagueID: string;
  leagueName: string;
  organizationID?: string;
  organizationName?: string;
  message?: string;
  inviterFirstName: string;
  inviterLastName: string;
  inviterEmail: string;
}

export function inviteTargetDisplayName(invite: SBotInviteSummary): string {
  return invite.organizationName ?? invite.leagueName;
}

export async function previewInvite(code: string): Promise<SBotInviteSummary> {
  const response = await apiRequest<{ invite: SBotInviteSummary }>("/invites/preview", { body: { code } });
  return response.invite;
}

export async function redeemInvite(
  code: string,
  makeActive: boolean,
  organizationAdminValue?: LeagueOrganizationAdminValue,
): Promise<{ inviteID: string; status: SBotInviteStatus }> {
  return apiRequest("/invites/redeem", {
    body: {
      code,
      makeActive,
      organizationAdminValue: organizationAdminValue ? organizationAdminValueFirestoreData(organizationAdminValue) : undefined,
    },
  });
}

export async function listInvites(leagueID: string): Promise<SBotInviteSummary[]> {
  const response = await apiRequest<{ invites: SBotInviteSummary[] }>(`/invites?leagueID=${encodeURIComponent(leagueID)}`);
  return response.invites;
}

export async function createInvite(draft: SBotInviteDraft): Promise<{ inviteID: string; invite?: SBotInviteSummary }> {
  return apiRequest("/invites", { body: { ...draft } });
}

export async function revokeInvite(inviteID: string): Promise<void> {
  await apiRequest(`/invites/${encodeURIComponent(inviteID)}/revoke`, { method: "POST", body: {} });
}

export async function resendInvite(inviteID: string): Promise<void> {
  await apiRequest(`/invites/${encodeURIComponent(inviteID)}/resend`, { method: "POST", body: {} });
}
